import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { changeUser, changeWallet } from "../../App/Slices/userSlice";
import { RootState } from "../../App/store";
import { Lobby } from "../../Interfaces/bucket/Lobby";
import { User } from "../../Interfaces/bucket/User";
import ContractService from "../../Services/ContractService";
import DataService from "../../Services/DataService";
import "./GameLayout.scss";
import { Subscription } from "rxjs";
import {
  changeGame,
  changeGamePlayers,
  changeJoinedLobby,
  changeLobby,
  changeStatus,
  userInteracted,
} from "../../App/Slices/gameSlice";
import { Game } from "../../Interfaces/bucket/Game";
import { closePopup, openPopup } from "../../App/Slices/popupSlice";
import GameClosed from "../../components/shared/GameClosed/GameClosed";
import { setOverlayContent } from "../../App/Slices/overlaySlice";
import Score from "../../PlayGame/Score/Score";
import { AudioTrack } from "../../components/AudioTrack/AudioTrack";
import { StartAlert } from "../../components/StartAlert/StartAlert";
import { CounterContext } from "../../contexts/counter";
import BchConnectorService from "../../Services/BchConnectorService";

const GameLayout = () => {
  const dispatch = useDispatch();
  const [isSubLobby, setIsSubLobby] = useState<boolean>(false);
  const [isSubUser, setIsSubUser] = useState<boolean>(false);
  const [isPageLoad, setIsPageLoad] = useState<boolean>(false);
  const userSelector = useSelector((state: RootState) => state.user);
  const gameSelector = useSelector((state: RootState) => state.game);
  const [lobby, setLobby] = useState<Lobby>();
  const [subsLobby, setSubsLobby] = useState<Subscription>();
  const navigate = useNavigate();

  useEffect(() => {
    if (userSelector.user._id && !isSubUser) {
      subscribeUser();
    }
  }, [userSelector.user, isSubUser]);

  const getWallet = async () => {
    try {
      const connectorType = localStorage.getItem("connectedOnce");
      let connectedAddress: string = "";
      if (connectorType === "Metamask") {
        connectedAddress = (await ContractService.sendConnectionRequest())?.[0];
      } else if (connectorType || 'null' !== 'null') {
        BchConnectorService.setConnector(connectorType as any);
        connectedAddress = (await BchConnectorService.sendConnectionRequest())!;
      }

      if (connectedAddress) {
        DataService.resources.user
          .get(localStorage.getItem("spicaUserId") as string)
          .then((data) => {
            dispatch(changeUser(data));
            dispatch(changeWallet(connectedAddress!));
          })
          .catch(() => {
            navigate("/authorization");
            localStorage.clear();
          });
      }
    } catch (error) {
      navigate("/authorization");
    }
  };

  const subscribeUser = () => {
    setIsSubUser(true);
    DataService.resources.user
      .getRealtime<User>(userSelector.user._id as string)
      .subscribe((user) => {
        dispatch(changeUser(user));
      });
  };

  const subscribeToLobby = () => {
    setIsSubLobby(true);
    let a = DataService.resources.Lobby.getAllRealtime({
      filter: { active: true, coin: userSelector.user.coin },
      sort: { _id: -1 },
      limit: 1,
    }).subscribe((data: Lobby[]) => {
      let lobby = data[0];
      if (!lobby) return;
      lobby = JSON.parse(JSON.stringify(lobby));
      setLobby(lobby);
    });
    setSubsLobby(a);
  };

  useEffect(() => {
    const me = gameSelector.lobby?.players?.find(
      (player) => player._id == userSelector.user._id
    );

    if (me && gameSelector.status == "stateless") {
      dispatch(changeStatus("pending"));
      dispatch(changeJoinedLobby(gameSelector.lobby?.lobby_id!));
    }
  }, [gameSelector.lobby, userSelector.user]);

  useEffect(() => {
    if (!lobby) return;
    handleChangeLobby(lobby);
  }, [lobby]);

  const handleChangeLobby = async (lobby: Lobby) => {
    lobby = JSON.parse(JSON.stringify(lobby));
    if (
      gameSelector.joinedLobbyId &&
      gameSelector.joinedLobbyId != lobby.lobby_id
    )
      return;

    if (lobby.players && lobby.players.length > 0) {
      const relationalPlayers: User[] = await DataService.resources.user.getAll(
        {
          queryParams: { filter: { _id: { $in: lobby.players } } },
        }
      );
      lobby.players = lobby.players.map((player) => {
        player = relationalPlayers.find(
          (relPlayer) => relPlayer._id == player
        ) as User;
        return player;
      });

      (lobby.players as User[]).forEach((player, i) => {
        player.image = `/assets/home/team-${i + 1}.png`;
        return player;
      });
    }
    dispatch(changeLobby(lobby));

    if (
      gameSelector.joinedLobbyId == lobby.lobby_id &&
      lobby.players?.length == lobby.player_count
    ) {
      dispatch(changeStatus("start"));
      dispatch(changeGamePlayers(lobby.players as User[]));
      return;
    }
  };

  useEffect(() => {
    if (isPageLoad) {
      if (
        !localStorage.getItem("spicaToken") ||
        !localStorage.getItem("spicaUserId")
      ) {
        navigate("/authorization");
        return;
      }
      getWallet();
    }
  }, [isPageLoad]);

  useEffect(() => {
    if (userSelector.user._id && isPageLoad && !isSubLobby) subscribeToLobby();
  }, [userSelector.user, isSubLobby]);

  useEffect(() => {
    setIsPageLoad(true);
  }, []);

  useEffect(() => {
    switch (gameSelector.status) {
      case "play":
        (window as any).startTheGame(
          { Id: userSelector.user._id },
          gameSelector.game?._id,
          gameSelector.game?.map
        );
        break;
      case "start":
        subsLobby?.unsubscribe();
        dispatch(changeJoinedLobby(0));

        DataService.resources.Game.getAllRealtime({
          filter: { game_id: gameSelector.lobby?.lobby_id },
        }).subscribe((data: Game[]) => {
          dispatch(changeGame(data[0]));
        });
        break;
    }
  }, [gameSelector.status]);

  useEffect(() => {
    if (!gameSelector.game) return;

    if (
      gameSelector.game.status == "CANCELLED" ||
      gameSelector.game.status == "KILLED"
    ) {
      dispatch(openPopup(<GameClosed></GameClosed>));
      dispatch(userInteracted(false));
      // setTimeout(() => {
      //   (window as any).quitGame();
      // }, 1500);
    }

    if (gameSelector.status == "start" && gameSelector.game.server_ready) {
      dispatch(changeStatus("play"));
    }

    if (gameSelector.status == "play" && gameSelector.game.status == "ENDED") {
      // setTimeout(() => {
      //   (window as any).quitGame();
      // }, 1500);
      (window as any).muteGame();
      dispatch(userInteracted(false));
      dispatch(changeStatus("stateless"));
      dispatch(setOverlayContent(renderScore));
    }
  }, [gameSelector.game, gameSelector.status]);

  const renderScore = useMemo(() => {
    return <Score game={gameSelector.game}></Score>;
  }, [gameSelector.game]);

  const showStartAlert = () => {
    setTimeout(() => {
      dispatch(openPopup(<StartAlert />));
    }, 100);
  };

  const counterContext = useContext(CounterContext);

  useEffect(() => {
    const isUserExistInLobby = gameSelector.lobby?.players?.some(
      (player) => player.wallet == userSelector.user.wallet
    );
    if (
      counterContext?.counter! <= 0 &&
      isUserExistInLobby &&
      gameSelector.status == "pending"
    ) {
      dispatch(closePopup(0));
      showStartAlert();
    }
  }, [counterContext?.counter]);

  const logout = useCallback(async () => {
    const connectorType = localStorage.getItem("connectedOnce");
    if (connectorType || 'null' !== 'null') {
      await BchConnectorService.disconnect();
    }

    localStorage.removeItem("connectedOnce");
    await BchConnectorService.disconnect();
    dispatch(changeWallet(undefined as any));
    dispatch(changeUser(undefined as any));
    localStorage.removeItem("spicaToken");
    localStorage.removeItem("spicaUserId");
    window.location.reload();
  }, []);

  return (
    <div className="GameLayout">
      <div className="Header">
        <div className="Header__logo">
          <img src="/assets/game/Logo.png" alt="" />
        </div>
        {userSelector.user._id && (
          <nav>
            <NavLink
              to="/"
              className={({ isActive }) => (isActive ? "active" : "")}
            >
              Lobby
            </NavLink>
            <NavLink
              to="profile"
              className={({ isActive }) => (isActive ? "active" : "")}
            >
              Profile
            </NavLink>
            <NavLink
              to="/authorization"
              onClick={(e) => { e.preventDefault(); logout();}}
            >
              Logout
            </NavLink>
          </nav>
        )}
      </div>
      <AudioTrack></AudioTrack>
      <Outlet></Outlet>
    </div>
  );
};

export default GameLayout;
